/**
 * AppLayout — authenticated shell.
 *
 * Layout:
 *   - Desktop (≥ 768px): persistent left Sidebar + main content area.
 *     CSS shifts the main content right by the sidebar width so they
 *     don't overlap.
 *   - Mobile: top header with brand + hamburger; Sidebar slides in
 *     over the content when toggled.
 *
 * Responsibilities here (not in Sidebar): auth gating wrapper, realtime
 * subscriptions, idle timeout, offline banner, biometric enroll prompt.
 * Sidebar.tsx owns the nav items + role gating.
 */

import { useState, type ReactNode }   from 'react'
import { Outlet }                      from 'react-router'
import { useTranslation }              from 'react-i18next'
import { useBrand }                    from '@/lib/brand/BrandProvider'
import { useAuthStore }                from '@/store/authStore'
import { RouteErrorBoundary }          from '@/components/ErrorBoundary'
import { OfflineBanner }               from '@/components/layout/OfflineBanner'
import { AiServiceStatusBanner }       from '@/components/layout/AiServiceStatusBanner'
import { Sidebar }                     from '@/components/layout/Sidebar'
import { OrgSwitcher }                 from '@/components/layout/OrgSwitcher'
import { RoleSwitcher }                from '@/components/layout/RoleSwitcher'
import { BiometricEnrollPrompt }       from '@/components/biometric/BiometricEnrollPrompt'
import { useRealtimeNotifications }    from '@/hooks/useRealtimeNotifications'
import { useRealtimeTaskAssignments }  from '@/hooks/useRealtimeTaskAssignments'
import { useIdleTimeout }              from '@/hooks/useIdleTimeout'
import './AppLayout.css'

export function AppLayout(): ReactNode {
  const { t }  = useTranslation()
  const brand  = useBrand()
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const [navOpen, setNavOpen] = useState(false)

  useRealtimeNotifications(userId)
  useRealtimeTaskAssignments(userId)
  useIdleTimeout()

  return (
    <div
      style={{
        minHeight:  '100vh',
        background: 'var(--brand-neutral-50)',
        color:      'var(--brand-neutral-900)',
        fontFamily: 'var(--brand-font-family)',
      }}
    >
      <OfflineBanner />
      <AiServiceStatusBanner />
      <BiometricEnrollPrompt />

      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />

      <div className="rro-mobile-header">
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          data-testid="nav-hamburger"
          aria-label={t('nav.openMenu', 'Open menu')}
          style={{
            padding:      '0.45rem 0.7rem',
            background:   'var(--brand-white)',
            color:        'var(--brand-neutral-700)',
            border:       '1px solid var(--brand-neutral-300)',
            borderRadius: 'var(--brand-radius-md)',
            fontSize:     '1.1rem',
            cursor:       'pointer',
            lineHeight:   1,
          }}
        >
          ☰
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {brand.assets.logoMarkUrl && (
            <img
              src={brand.assets.logoMarkUrl}
              alt=""
              width={28}
              height={28}
              style={{ borderRadius: 'var(--brand-radius-sm)', objectFit: 'contain' }}
            />
          )}
          <OrgSwitcher variant="compact" fallback={brand.name} />
          <RoleSwitcher variant="compact" />
        </div>
      </div>

      <main className="rro-main">
        <RouteErrorBoundary>
          <Outlet />
        </RouteErrorBoundary>
      </main>
    </div>
  )
}
